import { useState, useEffect } from 'react'
import {
  Button,
  Container,
  Divider,
  Grid,
  Link,
  Skeleton,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tabs,
  TextField,
  Typography,
} from '@mui/material'
import { PaymentsOutlined } from '@mui/icons-material'
import './PurchaseOrderList.css'
import axios from 'axios'
import { DatePicker } from '../helper'
import dayjs from 'dayjs'
import { Link as RouterLink } from 'react-router-dom'
import ErrorScreen from '../Misc/ErrorScreen'
import { purchaseOrderType } from '../types'

type purchaseOrderListType = purchaseOrderType & {
  vendor_name: string
  customer_name: string | null
  job_name: string | null
  so_number: string | null
}

function PurchaseOrderList() {
  const [purchaseOrderList, setPurchaseOrderList] = useState<purchaseOrderListType[]>([])
  const [purchaseOrderCount, setPurchaseOrderCount] = useState(0)
  const [purchaseOrderFilter, setPurchaseOrderFilter] = useState<{
    [index: string]: string | string[] | number | Date | undefined
  }>({
    purchaseOrderNumber: '',
    vendorName: '',
    customerName: '',
    salesOrderNumber: '',
    poDateFrom: undefined,
    poDateTo: undefined,
    dueDateFrom: undefined,
    dueDateTo: undefined,
    limit: 25,
    page: 0,
    sort: 'txn_date desc',
  })
  const [listLoading, setListLoading] = useState(true)
  const [initialLoad, setInitialLoad] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [searchError, setSearchError] = useState(false)

  useEffect(() => {
    handleSearch(purchaseOrderFilter)
    window.scrollTo(0, 0)
    document.title = 'Purchase Orders | Salestrack'
  }, [])

  const handleSearch = (filter: { [index: string]: string | string[] | number | Date | undefined }) => {
    let searchEndpoint = '/vendor?'
    for (const key of Object.keys(filter)) {
      if (filter[key]) {
        searchEndpoint += `${key}=${filter[key]}&`
      }
    }

    let loading = true
    setTimeout(() => {
      setListLoading(loading)
    }, 200)

    setSearchError(false)
    axios
      .get(searchEndpoint)
      .then(res => {
        setPurchaseOrderList(res.data.data)
        setPurchaseOrderCount(res.data.count)
        setLoadError(false)
        setSearchError(false)
      })
      .catch(error => {
        console.error(error)
        if (initialLoad) {
          setLoadError(true)
        } else {
          setSearchError(true)
        }
      })
      .finally(() => {
        loading = false
        setListLoading(false)
        setInitialLoad(false)
      })
  }

  const handleClear = () => {
    const newFilter = {
      ...purchaseOrderFilter,
      purchaseOrderNumber: '',
      vendorName: '',
      customerName: '',
      salesOrderNumber: '',
      poDateFrom: undefined,
      poDateTo: undefined,
      dueDateFrom: undefined,
      dueDateTo: undefined,
      sort: '',
    }
    setPurchaseOrderFilter(newFilter)
    handleSearch(newFilter)
  }

  if (loadError) {
    return (
      <div className="purchase-order-error-container">
        <ErrorScreen
          errorText="Failed to load purchase order list. Please try again or contact support if the error persists."
          title="Vendor Load Failed"
        />
      </div>
    )
  }

  return (
    <div className="purchase-order">
      <Container maxWidth={false} className="purchase-order-search-container">
        {initialLoad && listLoading ? (
          <>
            <div className="purchase-order-search-header">
              <Skeleton variant="circular" width={32} height={32} />
              <Skeleton width={180} height={32} />
            </div>
            <Divider />
            <Grid container spacing={2} className="purchase-order-search-grid">
              {[...Array(6)].map((_, idx) => (
                <Grid size={3} className="purchase-order-search-fields" key={idx}>
                  <Skeleton width={120} height={24} />
                  <Skeleton variant="rectangular" height={40} />
                </Grid>
              ))}
            </Grid>
            <div className="purchase-order-search-buttons">
              <Button variant="outlined" loading={listLoading}>
                Clear
              </Button>
              <Button variant="contained" type="submit" loading={listLoading}>
                Search
              </Button>
            </div>
          </>
        ) : (
          <>
            <Tabs value={'purchaseorder'}>
              <Tab value="purchaseorder" label="Purchase Orders" icon={<PaymentsOutlined />} iconPosition="start" />
            </Tabs>
            <Divider />
            <Grid container spacing={2} className="purchase-order-search-grid">
              <Grid size={3} className="purchase-order-search-fields">
                <Typography variant="body2">Vendor Purchase Order No:</Typography>
                <TextField
                  size="small"
                  fullWidth
                  label="Purchase Order Number"
                  value={purchaseOrderFilter.purchaseOrderNumber}
                  onChange={e =>
                    setPurchaseOrderFilter({ ...purchaseOrderFilter, purchaseOrderNumber: e.target.value })
                  }
                />
              </Grid>
              <Grid size={3} className="purchase-order-search-fields">
                <Typography variant="body2">Vendor Name:</Typography>
                <TextField
                  size="small"
                  fullWidth
                  label="Vendor"
                  value={purchaseOrderFilter.vendorName}
                  onChange={e => setPurchaseOrderFilter({ ...purchaseOrderFilter, vendorName: e.target.value })}
                />
              </Grid>
              <Grid size={3} className="purchase-order-search-fields">
                <Typography variant="body2">Customer Name:</Typography>
                <TextField
                  size="small"
                  fullWidth
                  label="Customer"
                  value={purchaseOrderFilter.customerName}
                  onChange={e => setPurchaseOrderFilter({ ...purchaseOrderFilter, customerName: e.target.value })}
                />
              </Grid>
              <Grid size={3} className="purchase-order-search-fields">
                <Typography variant="body2">Sales Order No:</Typography>
                <TextField
                  size="small"
                  fullWidth
                  label="Sales Order Number"
                  value={purchaseOrderFilter.salesOrderNumber}
                  onChange={e => setPurchaseOrderFilter({ ...purchaseOrderFilter, salesOrderNumber: e.target.value })}
                />
              </Grid>
              <Grid size={3} className="purchase-order-search-fields">
                <Typography variant="body2">Purchase Order Date:</Typography>
                <div className="purchase-order-date-search-container">
                  <DatePicker
                    label="From Date"
                    size="small"
                    value={
                      purchaseOrderFilter.poDateFrom
                        ? dayjs.utc(purchaseOrderFilter.poDateFrom as Date | undefined)
                        : null
                    }
                    onChange={e =>
                      setPurchaseOrderFilter({ ...purchaseOrderFilter, poDateFrom: e?.format('YYYY-MM-DD') })
                    }
                  />
                  <DatePicker
                    label="To Date"
                    size="small"
                    value={
                      purchaseOrderFilter.poDateTo ? dayjs.utc(purchaseOrderFilter.poDateTo as Date | undefined) : null
                    }
                    onChange={e =>
                      setPurchaseOrderFilter({ ...purchaseOrderFilter, poDateTo: e?.format('YYYY-MM-DD') })
                    }
                  />
                </div>
              </Grid>
              <Grid size={3} className="purchase-order-search-fields">
                <Typography variant="body2">Due Date:</Typography>
                <div className="purchase-order-date-search-container">
                  <DatePicker
                    label="From Date"
                    size="small"
                    value={
                      purchaseOrderFilter.dueDateFrom
                        ? dayjs.utc(purchaseOrderFilter.dueDateFrom as Date | undefined)
                        : null
                    }
                    onChange={e =>
                      setPurchaseOrderFilter({ ...purchaseOrderFilter, dueDateFrom: e?.format('YYYY-MM-DD') })
                    }
                  />
                  <DatePicker
                    label="To Date"
                    size="small"
                    value={
                      purchaseOrderFilter.dueDateTo
                        ? dayjs.utc(purchaseOrderFilter.dueDateTo as Date | undefined)
                        : null
                    }
                    onChange={e =>
                      setPurchaseOrderFilter({ ...purchaseOrderFilter, dueDateTo: e?.format('YYYY-MM-DD') })
                    }
                  />
                </div>
              </Grid>
            </Grid>
            <div className="purchase-order-search-buttons">
              <Button variant="outlined" onClick={handleClear} loading={initialLoad && listLoading}>
                Clear
              </Button>
              <Button
                variant="contained"
                type="submit"
                onClick={e => {
                  e.preventDefault()
                  handleSearch(purchaseOrderFilter)
                }}
                loading={initialLoad && listLoading}
              >
                Search
              </Button>
            </div>
          </>
        )}
      </Container>
      {searchError ? (
        <div className="purchase-order-list-error-container">
          <ErrorScreen
            errorText="Something went wrong while searching. Please try again or contact support if the error persists."
            title="Search Failed"
          />
        </div>
      ) : (
        <TableContainer className="purchase-order-list-table">
          <div className="purchase-order-list-table-header">
            {initialLoad && listLoading ? (
              <>
                <Skeleton width={180} height={32} />
                <Skeleton width={300} height={32} />
              </>
            ) : (
              <>
                <Typography variant="h6">Purchase Orders</Typography>
                <TablePagination
                  component="div"
                  count={purchaseOrderCount}
                  page={purchaseOrderFilter.page as number}
                  rowsPerPage={purchaseOrderFilter.limit as number}
                  onPageChange={(_e, newPage) => {
                    const newFilter = { ...purchaseOrderFilter, page: newPage }
                    setPurchaseOrderFilter(newFilter)
                    handleSearch(newFilter)
                  }}
                  onRowsPerPageChange={e => {
                    const newFilter = { ...purchaseOrderFilter, limit: parseInt(e.target.value) }
                    setPurchaseOrderFilter(newFilter)
                    handleSearch(newFilter)
                  }}
                />
              </>
            )}
          </div>
          <Table stickyHeader color="primary">
            <TableHead>
              {initialLoad && listLoading ? (
                <TableRow>
                  {Array.from({ length: 8 }).map((_, idx) => (
                    <TableCell key={idx}>
                      <Skeleton className="skeleton-header" />
                    </TableCell>
                  ))}
                </TableRow>
              ) : (
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={(purchaseOrderFilter.sort as string).includes('impres_po')}
                      direction={purchaseOrderFilter.sort === 'impres_po' ? 'asc' : 'desc'}
                      onClick={() => {
                        const newFilter = {
                          ...purchaseOrderFilter,
                          sort: purchaseOrderFilter.sort === 'impres_po' ? 'impres_po desc' : 'impres_po',
                        }
                        setPurchaseOrderFilter(newFilter)
                        handleSearch(newFilter)
                      }}
                    >
                      IMPRES PO
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={(purchaseOrderFilter.sort as string).includes('vendor_name')}
                      direction={purchaseOrderFilter.sort === 'vendor_name' ? 'asc' : 'desc'}
                      onClick={() => {
                        const newFilter = {
                          ...purchaseOrderFilter,
                          sort: purchaseOrderFilter.sort === 'vendor_name' ? 'vendor_name desc' : 'vendor_name',
                        }
                        setPurchaseOrderFilter(newFilter)
                        handleSearch(newFilter)
                      }}
                    >
                      Vendor Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={(purchaseOrderFilter.sort as string).includes('customer_name')}
                      direction={purchaseOrderFilter.sort === 'customer_name' ? 'asc' : 'desc'}
                      onClick={() => {
                        const newFilter = {
                          ...purchaseOrderFilter,
                          sort: purchaseOrderFilter.sort === 'customer_name' ? 'customer_name desc' : 'customer_name',
                        }
                        setPurchaseOrderFilter(newFilter)
                        handleSearch(newFilter)
                      }}
                    >
                      Customer Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={(purchaseOrderFilter.sort as string).includes('job_name')}
                      direction={purchaseOrderFilter.sort === 'job_name' ? 'asc' : 'desc'}
                      onClick={() => {
                        const newFilter = {
                          ...purchaseOrderFilter,
                          sort: purchaseOrderFilter.sort === 'job_name' ? 'job_name desc' : 'job_name',
                        }
                        setPurchaseOrderFilter(newFilter)
                        handleSearch(newFilter)
                      }}
                    >
                      Job Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={(purchaseOrderFilter.sort as string).includes('so_number')}
                      direction={purchaseOrderFilter.sort === 'so_number' ? 'asc' : 'desc'}
                      onClick={() => {
                        const newFilter = {
                          ...purchaseOrderFilter,
                          sort: purchaseOrderFilter.sort === 'so_number' ? 'so_number desc' : 'so_number',
                        }
                        setPurchaseOrderFilter(newFilter)
                        handleSearch(newFilter)
                      }}
                    >
                      SO Number
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={(purchaseOrderFilter.sort as string).includes('txn_date')}
                      direction={purchaseOrderFilter.sort === 'txn_date' ? 'asc' : 'desc'}
                      onClick={() => {
                        const newFilter = {
                          ...purchaseOrderFilter,
                          sort: purchaseOrderFilter.sort === 'txn_date' ? 'txn_date desc' : 'txn_date',
                        }
                        setPurchaseOrderFilter(newFilter)
                        handleSearch(newFilter)
                      }}
                    >
                      PO Date
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={(purchaseOrderFilter.sort as string).includes('due_date')}
                      direction={purchaseOrderFilter.sort === 'due_date' ? 'asc' : 'desc'}
                      onClick={() => {
                        const newFilter = {
                          ...purchaseOrderFilter,
                          sort: purchaseOrderFilter.sort === 'due_date' ? 'due_date desc' : 'due_date',
                        }
                        setPurchaseOrderFilter(newFilter)
                        handleSearch(newFilter)
                      }}
                    >
                      Due Date
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={(purchaseOrderFilter.sort as string).includes('total_amount')}
                      direction={purchaseOrderFilter.sort === 'total_amount' ? 'asc' : 'desc'}
                      onClick={() => {
                        const newFilter = {
                          ...purchaseOrderFilter,
                          sort: purchaseOrderFilter.sort === 'total_amount' ? 'total_amount desc' : 'total_amount',
                        }
                        setPurchaseOrderFilter(newFilter)
                        handleSearch(newFilter)
                      }}
                    >
                      Total Amount
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              )}
            </TableHead>
            <TableBody>
              {listLoading
                ? Array.from({ length: purchaseOrderFilter.limit ? Number(purchaseOrderFilter.limit) : 25 }).map(
                    (_, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Skeleton />
                        </TableCell>
                        <TableCell>
                          <Skeleton />
                        </TableCell>
                        <TableCell>
                          <Skeleton />
                        </TableCell>
                        <TableCell>
                          <Skeleton />
                        </TableCell>
                        <TableCell align="right">
                          <Skeleton />
                        </TableCell>
                        <TableCell align="right">
                          <Skeleton />
                        </TableCell>
                        <TableCell align="right">
                          <Skeleton />
                        </TableCell>
                        <TableCell align="right">
                          <Skeleton />
                        </TableCell>
                      </TableRow>
                    ),
                  )
                : purchaseOrderList.map(purchaseOrder => (
                    <TableRow key={purchaseOrder.purchase_order_txn_id}>
                      <TableCell>
                        <Link
                          component={RouterLink}
                          to={`/vendor/purchaseorder/${purchaseOrder.purchase_order_txn_id}`}
                        >
                          {purchaseOrder.ref_number || '-'}
                        </Link>
                      </TableCell>
                      <TableCell>{purchaseOrder.vendor_name || '-'}</TableCell>
                      <TableCell>{purchaseOrder.customer_name || '-'}</TableCell>
                      <TableCell>{purchaseOrder.job_name || '-'}</TableCell>
                      <TableCell align="right">{purchaseOrder.so_number || '-'}</TableCell>
                      <TableCell align="right">{dayjs.utc(purchaseOrder.txn_date).format('MM/DD/YYYY')}</TableCell>
                      <TableCell align="right">{dayjs.utc(purchaseOrder.due_date).format('MM/DD/YYYY')}</TableCell>
                      <TableCell align="right">
                        {purchaseOrder.total_amount
                          ? purchaseOrder.total_amount.toLocaleString('en-US', {
                              style: 'currency',
                              currency: 'USD',
                            })
                          : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
            </TableBody>
          </Table>
          <TablePagination
            className="purchase-order-list-table-pagination-footer"
            component="div"
            count={purchaseOrderCount}
            page={purchaseOrderFilter.page as number}
            rowsPerPage={purchaseOrderFilter.limit as number}
            onPageChange={(_e, newPage) => {
              const newFilter = { ...purchaseOrderFilter, page: newPage }
              setPurchaseOrderFilter(newFilter)
              handleSearch(newFilter)
            }}
            onRowsPerPageChange={e => {
              const newFilter = { ...purchaseOrderFilter, limit: parseInt(e.target.value) }
              setPurchaseOrderFilter(newFilter)
              handleSearch(newFilter)
            }}
          />
        </TableContainer>
      )}
    </div>
  )
}

export default PurchaseOrderList
